import { getDevice } from '@/hooks/useDevice';
import { isSsr } from '@/utilities/Utilities';
import store from '../store';
import { CampaignState } from '@/models/CampaignModel';
import { getCookie, setCookie } from '@/utilities/CookieHelpers';
import { parseQueryString } from '@/utilities/Url';
import useAxios from './useAxios';
import { CookieCategory, waitForCookieAccess } from '@/services/cookieConsent';

interface AnalyticsQuery {
  cmpid: number;
  tz: string;
  pid?: number;
  sid?: string;
  abid?: number;
  ref?: string;
  rid?: number;
  usour?: string;
  umedi?: string;
  ucamp?: string;
  uterm?: string;
  ucont?: string;
  d?: string | null; //Device
}

interface AnalyticsResponse {
  sid?: string;
}

// TODO: Move this into a store
let sessionId: string | null = null;
let sessionLastSeen: number | null = null;
const tracked: number[] = [];

export default function useAnalytics() {
  const campaignState = store.getters['campaign/state'] as CampaignState;
  const cookiePrefix = campaignState?.config?.campaignId + '-sid';
  const sessionTimeout = 60 * 5;
  const sessionTimeoutInSession = 60 * 5;

  sessionLastSeen = sessionLastSeen ?? getCookie(cookiePrefix + '-seen') ?? null;
  sessionId = sessionId ?? getCookie(cookiePrefix) ?? null;

  if (sessionId && !(sessionLastSeen && Math.floor((new Date().getTime() - sessionLastSeen) / 1000) < sessionTimeout)) {
    sessionId = null;
  }

  const handleSaveSession = async () => {
    const expires = new Date();
    expires.setTime(expires.getTime() + 5 * 60 * 1000);
    const cookieOptions = { expire: expires };
    sessionLastSeen = new Date().getTime();
    await waitForCookieAccess(CookieCategory.STATISTICS);
    setCookie(cookiePrefix, sessionId ?? '', cookieOptions, true);
    setCookie(cookiePrefix + '-seen', sessionLastSeen ? sessionLastSeen.toString() : '', cookieOptions, true);
  };
  const isDemo = () => {
    return (store.getters['utility/path'] ?? window.location.href).includes('/campaign/view/demo');
  };

  const handlePageView = async (pageId?: number) => {
    if (isSsr() || (pageId && tracked.includes(pageId)) || campaignState?.config?.expired) {
      return;
    }

    if (
      sessionId &&
      !(sessionLastSeen && Math.floor((new Date().getTime() - sessionLastSeen) / 1000) < sessionTimeoutInSession)
    ) {
      sessionId = null;
    }

    if (pageId) {
      tracked.push(pageId);
    }

    if (
      !isDemo() &&
      campaignState?.config?.customerId &&
      campaignState?.config?.campaignId &&
      campaignState?.config?.analytics?.basePath
    ) {
      const registrationId = store.state.global.flowRegistrationInfo?.id;
      const docReferrer = document.referrer;
      let queryParams = parseQueryString(window.location.href);
      if (!queryParams || Object.entries(queryParams).length < 1) {
        const storeUrl = store.getters['utility/currentUrl'];
        if (storeUrl) {
          queryParams = parseQueryString(storeUrl);
        }
      }
      const utmSource = queryParams.utm_source;
      const utmMedium = queryParams.utm_medium;
      const utmCampaign = queryParams.utm_campaign;
      const utmTerm = queryParams.utm_term;
      const utmContent = queryParams.utm_content;

      const query: AnalyticsQuery = {
        cmpid: campaignState?.config?.campaignId,
        tz: campaignState?.config?.timezone,
        pid: pageId
      };

      if (sessionId !== null) {
        query.sid = sessionId;
      }

      // Exclude referrer on popup
      if (docReferrer && ['popup', 'popupv2'].indexOf(campaignState?.config.gameAlias) === -1) {
        // Exclude query string from referrer
        query.ref = (docReferrer + '').split('?')[0];
      }

      if (registrationId) {
        query.rid = registrationId;
      }

      if (utmSource) {
        query.usour = utmSource;
      }

      if (utmMedium) {
        query.umedi = utmMedium;
      }

      if (utmCampaign) {
        query.ucamp = utmCampaign;
      }

      if (utmTerm) {
        query.uterm = utmTerm;
      }

      if (utmContent) {
        query.ucont = utmContent;
      }

      query.d = getDevice()[0];

      const { fetchData } = useAxios<AnalyticsResponse>(
        `${campaignState?.config.analytics.basePath}/api/v1/campaign/analytic/page-view`,
        query
      );
      const response = await fetchData();
      if (response && response.sid) {
        sessionId = response.sid;
        handleSaveSession();
      }
    }
  };

  return {
    handlePageView
  };
}
